import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useState } from "react";
import { FaFacebookF, FaInstagram } from "react-icons/fa";
import { TbClockHour3 } from "react-icons/tb";
import { CiMail } from "react-icons/ci";
import { IoLocationOutline } from "react-icons/io5";
import { LuBadgeCheck, LuTriangleAlert, LuLocateFixed } from "react-icons/lu";
import visitaxlogo from "../../../../assets/images/HomePage/VISITAX.webp";
import logogob from "../../../../assets/images/HomePage/LOGO GOBIERNO.webp";
import footerInfoBg from "../../../../assets/images/HomePage/homePageImg.webp";
import { useConfigurations } from "../../../../context/ConfigurationsContext/ConfigurationsContext";
import { usePrincipalPage } from "../../../../context/PrincipalPageContext/PrincipalPageContext";
import ModalText from "../../../../components/composed-styled-components/ModalText/ModalText";
import ModalTextPrivacity from "../../../../components/composed-styled-components/ModalTextPrivacity/ModalTextPrivacity";
import VersionComponent from "../../../../components/basic-styled-components/Version/Version";

const getFooterLinkStyle = (isMobile: boolean) => ({
  color: "#FFFFFF",
  fontFamily: "Candara",
  fontSize: "16px",
  lineHeight: isMobile ? "20px" : "24px",
  textDecoration: "none",
  textTransform: "none" as const,
});

type FooterLocale = "en" | "es" | "pt" | "fr" | "de" | "it" | "zh";

const COPYRIGHT_TEXT: Record<FooterLocale, string> = {
  en: "Copyright 2026 Visitax Online. All rights reserved.",
  es: "Copyright 2026 Visitax Online. Todos los derechos reservados.",
  pt: "Copyright 2026 Visitax Online. Todos os direitos reservados.",
  fr: "Copyright 2026 Visitax Online. Tous droits réservés.",
  de: "Copyright 2026 Visitax Online. Alle Rechte vorbehalten.",
  it: "Copyright 2026 Visitax Online. Tutti i diritti riservati.",
  zh: "Copyright 2026 Visitax Online. 保留所有权利。",
};

const normalizeLanguageName = (languageName = "") => {
  try {
    return languageName
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  } catch {
    return languageName.toLowerCase();
  }
};

const resolveFooterLocale = (
  languageName?: string,
  flag?: string,
): FooterLocale => {
  const normalizedName = normalizeLanguageName(languageName);
  const upperFlag = (flag ?? "").toUpperCase();

  if (
    normalizedName.includes("espanol") ||
    normalizedName.includes("spanish") ||
    normalizedName === "es" ||
    normalizedName.startsWith("es-") ||
    ["MX", "ES", "AR", "CO", "CL", "PE"].includes(upperFlag)
  ) {
    return "es";
  }

  if (
    normalizedName.includes("portugues") ||
    normalizedName.includes("portuguese") ||
    normalizedName === "pt" ||
    normalizedName.startsWith("pt-") ||
    ["BR", "PT"].includes(upperFlag)
  ) {
    return "pt";
  }

  if (
    normalizedName.includes("deutsch") ||
    normalizedName.includes("german") ||
    normalizedName === "de" ||
    normalizedName.startsWith("de-") ||
    ["DE", "AT", "CH"].includes(upperFlag)
  ) {
    return "de";
  }

  if (
    normalizedName.includes("italiano") ||
    normalizedName.includes("italian") ||
    normalizedName === "it" ||
    normalizedName.startsWith("it-") ||
    upperFlag === "IT"
  ) {
    return "it";
  }

  if (
    normalizedName.includes("chinese") ||
    normalizedName.includes("中文") ||
    normalizedName === "zh" ||
    normalizedName.startsWith("zh-") ||
    ["CN", "TW", "HK"].includes(upperFlag)
  ) {
    return "zh";
  }

  if (
    normalizedName.includes("francais") ||
    normalizedName.includes("french") ||
    normalizedName === "fr" ||
    normalizedName.startsWith("fr-") ||
    upperFlag === "FR"
  ) {
    return "fr";
  }

  return "en";
};

const PrincipalPageFooter = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openPrivacity, setOpenPrivacity] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);
  const configurations = useConfigurations();
  const principalPage = usePrincipalPage();
  const txt = configurations.txt[configurations.currentlenguage];
  const footerLocale = resolveFooterLocale(
    configurations.lenguageList[configurations.currentlenguage],
    configurations.flags[configurations.currentlenguage],
  );
  const copyrightText = COPYRIGHT_TEXT[footerLocale];
  const infoHeadline =
    txt[181] ||
    "Visitax Online is the only official provider authorized to collect the Visitax payment.";
  const infoParagraph1 =
    txt[182] ||
    "We are the only company with a physical payment location inside Cancún International Airport, where you can make your payment quickly, safely, and with personalized assistance.";
  const infoParagraph2 =
    txt[183] || "Our staff is uniformed and fully trained to help you.";
  const infoCard1 =
    txt[187] ||
    "Visitax Online is the only authorized physical and digital channel.";
  const infoCard2 =
    txt[188] ||
    "Pay only through officially identified Visitax Online staff inside the airport — 100% certified.";
  const contactEmailText = (txt[147] || "support@visitaxonline.com").trim();
  const supportHoursText = (
    txt[148] || "Monday to Friday, 8:00 AM – 5:00 PM"
  ).trim();
  const emailIncludesSentence = /\s/.test(
    contactEmailText.replace(/support@visitaxonline\.com/i, "").trim(),
  );
  const hoursIncludesTitle = /support/i.test(supportHoursText);
  const handleDestinationsClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    event.preventDefault();
    principalPage.scrollToSection(principalPage.destinosRef);
  };

  return (
    <Box id="footer" sx={{ width: "100%" }}>
      <ModalTextPrivacity
        open={openPrivacity}
        onClose={() => setOpenPrivacity(false)}
        title={
          configurations.currentlenguage === 0
            ? "Privacy Notice"
            : "Aviso de privacidad"
        }
        text={
          configurations.currentlenguage === 0
            ? "/privacitynoticeenglish.txt"
            : "/privacitynoticespanish.txt"
        }
      />
      <ModalText open={openTerms} setOpen={setOpenTerms} />

      <Box
        sx={{
          backgroundImage: `linear-gradient(179.822deg, rgba(0, 0, 0, 0) 0.55784%, rgba(0, 0, 0, 0.25) 51.952%), linear-gradient(90deg, rgba(0, 89, 106, 0.7) 0%, rgba(0, 89, 106, 0.7) 100%), url(${footerInfoBg})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
          py: { xs: "28px", md: "67px" },
          px: { xs: 2.5, md: 2 },
        }}
      >
        <Box sx={{ margin: "0 auto", maxWidth: "918px", textAlign: "center" }}>
          <Typography
            sx={{
              color: "#FFFFFF",
              fontFamily: "Candara",
              fontSize: { xs: "16px", md: "18px" },
              fontWeight: 700,
              lineHeight: { xs: 1.2, md: "20px" },
            }}
          >
            {infoHeadline}
          </Typography>

          <Typography
            sx={{
              color: "#FFFFFF",
              fontFamily: "Candara",
              fontSize: { xs: "14px", md: "16px" },
              fontWeight: 400,
              lineHeight: { xs: 1.2, md: "20px" },
              mt: { xs: "16px", md: "25px" },
            }}
          >
            {infoParagraph1}
          </Typography>

          <Typography
            sx={{
              color: "#FFFFFF",
              fontFamily: "Candara",
              fontSize: { xs: "14px", md: "16px" },
              fontWeight: 400,
              lineHeight: { xs: 1.2, md: "20px" },
              mt: { xs: "10px", md: "12px" },
            }}
          >
            {infoParagraph2}
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row", md: "row" }}
            spacing={{ xs: 2, md: "60px" }}
            justifyContent="center"
            sx={{ mt: { xs: "20px", md: "30px" } }}
          >
            <Box
              sx={{
                alignItems: "flex-start",
                display: "flex",
                gap: { xs: "12px", md: "18px" },
                maxWidth: { xs: "250px", md: "254px" },
                mx: "auto",
                textAlign: "left",
              }}
            >
              <LuTriangleAlert
                color="#FFFFFF"
                size={isMobile ? 24 : 32}
                style={{ flexShrink: 0, marginTop: "2px" }}
              />
              <Typography
                sx={{
                  color: "#FFFFFF",
                  fontFamily: "Candara",
                  fontSize: { xs: "14px", md: "16px" },
                  fontWeight: 400,
                  lineHeight: { xs: 1.2, md: "20px" },
                }}
              >
                {infoCard1}
              </Typography>
            </Box>
            <Box
              sx={{
                alignItems: "flex-start",
                display: "flex",
                gap: { xs: "12px", md: "18px" },
                maxWidth: { xs: "250px", md: "254px" },
                mx: "auto",
                textAlign: "left",
              }}
            >
              <LuBadgeCheck
                color="#FFFFFF"
                size={isMobile ? 24 : 32}
                style={{ flexShrink: 0, marginTop: "2px" }}
              />
              <Typography
                sx={{
                  color: "#FFFFFF",
                  fontFamily: "Candara",
                  fontSize: { xs: "14px", md: "16px" },
                  fontWeight: 400,
                  lineHeight: { xs: 1.2, md: "20px" },
                }}
              >
                {infoCard2}
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: { xs: "24px", md: "30px" },
            }}
          >
            <a
              href="/Payment"
              style={{
                alignItems: "center",
                backgroundColor: "#943663",
                borderRadius: "24px",
                color: "#FFFFFF",
                display: "inline-flex",
                fontFamily: "Candara",
                fontSize: isMobile ? "14px" : "16px",
                fontWeight: 700,
                height: isMobile ? "46px" : "48px",
                justifyContent: "center",
                lineHeight: "20px",
                minWidth: isMobile ? "180px" : "200px",
                padding: "0 20px",
                textDecoration: "none",
              }}
            >
              {txt[131]}
            </a>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: "#761540",
          pt: { xs: "30px", md: "39px" },
          width: "100%",
        }}
      >
        {isMobile ? (
          <Box sx={{ margin: "0 auto", maxWidth: "360px", width: "100%" }}>
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ alignItems: "center", display: "flex", gap: "12px" }}>
                <Box
                  component="img"
                  src={visitaxlogo}
                  alt="VISITAX"
                  loading="lazy"
                  decoding="async"
                  sx={{ height: "36px", width: "80px" }}
                />
                <Box
                  component="img"
                  src={logogob}
                  alt="Gobierno"
                  loading="lazy"
                  decoding="async"
                  sx={{ height: "36px", width: "130px" }}
                />
              </Box>
            </Box>

            <Stack
              spacing="30px"
              sx={{ alignItems: "center", mt: "30px", textAlign: "center" }}
            >
              <Box sx={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenPrivacity(true);
                    setOpenTerms(false);
                  }}
                  style={getFooterLinkStyle(isMobile)}
                >
                  {txt[62]}
                </a>
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenTerms(true);
                    setOpenPrivacity(false);
                  }}
                  style={getFooterLinkStyle(isMobile)}
                >
                  {txt[198]}
                </a>
              </Box>

              <Box>
                <Box
                  sx={{
                    border: "1px solid #FFFFFF",
                    borderRadius: "4px",
                    display: "inline-flex",
                    p: "2px 6px",
                  }}
                >
                  <CiMail
                    color="#FFFFFF"
                    size={18}
                    aria-hidden="true"
                    focusable="false"
                  />
                </Box>
                {emailIncludesSentence ? (
                  <Typography
                    sx={{
                      color: "#FFFFFF",
                      fontFamily: "Candara",
                      fontSize: "16px",
                      lineHeight: 1.2,
                      mt: 1,
                    }}
                  >
                    {contactEmailText}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      sx={{
                        color: "#FFFFFF",
                        fontFamily: "Candara",
                        fontSize: "16px",
                        lineHeight: 1.2,
                        mt: 1,
                      }}
                    >
                      {txt[196]}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#FFFFFF",
                        fontFamily: "Candara",
                        fontSize: "16px",
                        lineHeight: 1.2,
                      }}
                    >
                      {contactEmailText}
                    </Typography>
                  </>
                )}
              </Box>

              <Box>
                <TbClockHour3
                  color="#FFFFFF"
                  size={28}
                  aria-hidden="true"
                  focusable="false"
                />
                {hoursIncludesTitle ? (
                  <Typography
                    sx={{
                      color: "#FFFFFF",
                      fontFamily: "Candara",
                      fontSize: "16px",
                      lineHeight: 1.2,
                      mt: 1,
                    }}
                  >
                    {supportHoursText}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      sx={{
                        color: "#FFFFFF",
                        fontFamily: "Candara",
                        fontSize: "16px",
                        lineHeight: 1.2,
                        mt: 1,
                      }}
                    >
                      {txt[197]}:
                    </Typography>
                    <Typography
                      sx={{
                        color: "#FFFFFF",
                        fontFamily: "Candara",
                        fontSize: "16px",
                        lineHeight: 1.2,
                      }}
                    >
                      {supportHoursText}
                    </Typography>
                  </>
                )}
              </Box>

              <Box>
                <LuLocateFixed
                  color="#FFFFFF"
                  size={28}
                  aria-hidden="true"
                  focusable="false"
                />
                <Typography
                  sx={{
                    color: "#FFFFFF",
                    fontFamily: "Candara",
                    fontSize: "16px",
                    lineHeight: 1.2,
                    mt: 1,
                  }}
                >
                  Tacubaya, Miguel
                  <br />
                  Hidalgo, 11870 Ciudad de México, CDMX
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: "#FFFFFF",
                    fontFamily: "Candara",
                    fontSize: "16px",
                    lineHeight: 1.2,
                  }}
                >
                  Follow us
                </Typography>
                <Stack
                  direction="row"
                  spacing="22px"
                  sx={{ justifyContent: "center", mt: 1.5 }}
                >
                  <a href="https://facebook.com/visitaxonline" target="_blank" aria-label="Facebook">
                    <FaFacebookF
                      color="#FFFFFF"
                      size={30}
                      aria-hidden="true"
                      focusable="false"
                    />
                  </a>
                  <a href="https://instagram.com/visitaxonline" target="_blank" aria-label="Instagram">
                    <FaInstagram
                      color="#FFFFFF"
                      size={30}
                      aria-hidden="true"
                      focusable="false"
                    />
                  </a>
                </Stack>
              </Box>
            </Stack>

            <Box
              sx={{ borderTop: "1px solid #FFFFFF", mt: "28px", width: "100%" }}
            />
            <Typography
              sx={{
                color: "#FFFFFF",
                fontFamily: "Candara",
                fontSize: "14px",
                lineHeight: 1.2,
                py: "20px",
                textAlign: "center",
                wordBreak: "break-word",
              }}
            >
              {copyrightText}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              margin: "0 auto",
              maxWidth: "1180px",
              px: { xs: 2.5, md: 0 },
              width: "100%",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={{ xs: "28px", md: 2 }}
              sx={{
                alignItems: { xs: "flex-start", md: "flex-start" },
                mb: { xs: "34px", md: "56px" },
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: { xs: "14px", md: "20px" },
                }}
              >
                <Box
                  component="img"
                  src={visitaxlogo}
                  alt="VISITAX"
                  loading="lazy"
                  decoding="async"
                  sx={{
                    height: { xs: "36px", md: "44px" },
                    width: { xs: "80px", md: "97px" },
                  }}
                />
                <Box
                  component="img"
                  src={logogob}
                  alt="Gobierno"
                  loading="lazy"
                  decoding="async"
                  sx={{
                    height: { xs: "36px", md: "44px" },
                    width: { xs: "130px", md: "160px" },
                  }}
                />
              </Box>

              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: isMobile ? "16px" : "24px",
                  textAlign: isMobile ? "left" : "center",
                }}
              >
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenPrivacity(true);
                    setOpenTerms(false);
                  }}
                  style={getFooterLinkStyle(isMobile)}
                >
                  {txt[62]}
                </a>
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenTerms(true);
                    setOpenPrivacity(false);
                  }}
                  style={getFooterLinkStyle(isMobile)}
                >
                  {txt[198]}
                </a>
                <a
                  href="/#destinations"
                  onClick={handleDestinationsClick}
                  style={getFooterLinkStyle(isMobile)}
                >
                  {txt[122]}
                </a>
                <a href="/FAQS" style={getFooterLinkStyle(isMobile)}>
                  {txt[199]}
                </a>
                <a href="/Blog" style={getFooterLinkStyle(isMobile)}>
                  {txt[173] || "Blog"}
                </a>
              </div>
            </Stack>

            <Stack
              spacing={{ xs: "18px", md: "24px" }}
              sx={{
                alignItems: { xs: "flex-start", sm: "center", md: "center" },
                mb: "18px",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "column", md: "row" }}
                justifyContent="space-between"
                alignItems="center"
                spacing={{ xs: 3, sm: "50px", md: "50px" }}
                sx={{
                  textAlign: { xs: "left", sm: "center", md: "center" },
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    maxWidth: "264px",
                    mx: { xs: 0, sm: "auto", md: "auto" },
                  }}
                >
                  <CiMail
                    color="#FFFFFF"
                    size={isMobile ? 24 : 30}
                    aria-hidden="true"
                    focusable="false"
                  />
                  {emailIncludesSentence ? (
                    <p
                      style={{
                        color: "#FFFFFF",
                        fontSize: "16px",
                        fontWeight: 400,
                        lineHeight: isMobile ? "20px" : "24px",
                        marginTop: "4px",
                      }}
                    >
                      {contactEmailText}
                    </p>
                  ) : (
                    <>
                      <p
                        style={{
                          color: "#FFFFFF",
                          fontSize: "16px",
                          fontWeight: 400,
                          lineHeight: isMobile ? "20px" : "24px",
                          marginTop: "4px",
                        }}
                      >
                        {txt[196]}
                      </p>
                      <p
                        style={{
                          color: "#FFFFFF",
                          fontSize: "16px",
                          fontWeight: 400,
                          lineHeight: isMobile ? "20px" : "24px",
                          marginTop: "4px",
                        }}
                      >
                        {contactEmailText}
                      </p>
                    </>
                  )}
                </Box>

                <Box
                  sx={{
                    maxWidth: "323px",
                    mx: { xs: 0, sm: "auto", md: "auto" },
                  }}
                >
                  <TbClockHour3
                    color="#FFFFFF"
                    size={isMobile ? 24 : 28}
                    aria-hidden="true"
                    focusable="false"
                  />
                  {hoursIncludesTitle ? (
                    <p
                      style={{
                        color: "#FFFFFF",
                        fontSize: "16px",
                        fontWeight: 400,
                        lineHeight: isMobile ? "20px" : "24px",
                        marginTop: "4px",
                      }}
                    >
                      {supportHoursText}
                    </p>
                  ) : (
                    <>
                      <p
                        style={{
                          color: "#FFFFFF",
                          fontSize: "16px",
                          fontWeight: 400,
                          lineHeight: isMobile ? "20px" : "24px",
                          marginTop: "4px",
                        }}
                      >
                        {txt[197]}
                      </p>
                      <p
                        style={{
                          color: "#FFFFFF",
                          fontSize: "16px",
                          fontWeight: 400,
                          lineHeight: isMobile ? "20px" : "24px",
                          marginTop: "4px",
                        }}
                      >
                        {supportHoursText}
                      </p>
                    </>
                  )}
                </Box>

                <Box
                  sx={{
                    maxWidth: "323px",
                    mx: { xs: 0, sm: "auto", md: "auto" },
                  }}
                >
                  <IoLocationOutline
                    color="#FFFFFF"
                    size={isMobile ? 24 : 28}
                    aria-hidden="true"
                    focusable="false"
                  />
                  <p
                    style={{
                      color: "#FFFFFF",
                      fontSize: "16px",
                      fontWeight: 400,
                      lineHeight: isMobile ? "20px" : "24px",
                      marginTop: "4px",
                    }}
                  >
                    Tacubaya, Miguel
                    <br />
                    Hidalgo, 11870 Ciudad de Mexico, CDMX
                  </p>
                </Box>
              </Stack>
            </Stack>

            <Stack
              spacing="16px"
              sx={{
                marginBottom: "16px",
                alignItems: { xs: "flex-start", sm: "center", md: "center" },
              }}
            >
              <p
                style={{
                  color: "#FFFFFF",
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: isMobile ? "20px" : "24px",
                  marginTop: "4px",
                }}
              >
                {txt[145]}
              </p>
              <Stack
                direction="row"
                spacing={{ xs: "18px", sm: "24px", md: "24px" }}
              >
                <a
                  href="https://facebook.com/visitaxonline"
                  aria-label="Facebook"
                  target="_blank"
                >
                  <FaFacebookF
                    color="#FFFFFF"
                    size={24}
                    aria-hidden="true"
                    focusable="false"
                  />
                </a>
                <a
                  href="https://instagram.com/visitaxonline"
                  aria-label="Instagram"
                  target="_blank"
                >
                  <FaInstagram
                    color="#FFFFFF"
                    size={24}
                    aria-hidden="true"
                    focusable="false"
                  />
                </a>
              </Stack>
            </Stack>

            <Box sx={{ borderTop: "1px solid #FFFFFF", width: "100%" }} />

            <Typography
              sx={{
                color: "#FFFFFF",
                fontFamily: "Candara",
                fontSize: { xs: "12px", md: "14px" },
                fontWeight: 400,
                lineHeight: { xs: "16px", md: "20px" },
                py: { xs: "18px", md: "24px" },
                textAlign: { xs: "left", md: "center" },
              }}
            >
              {copyrightText}
            </Typography>

            <VersionComponent />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PrincipalPageFooter;
